import {
    Box,
    Text,
    VStack,
    HStack,
    Flex,
    Grid,
    Icon,
    Table,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Button,
} from '@chakra-ui/react';
import { TbDots, TbCopy, TbCircleCheck, TbCircleX } from 'react-icons/tb';
import { FiShoppingCart } from 'react-icons/fi';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { formatPrice, formatPercentage } from '../../functions';
import ListPrice from '../ListPrice';
import SelectionStatusIcon from '../SelectionStatusIcon';
import { SelectionPart } from '../../hooks/useGetOrderDrawer';
import StatBox from '../StatBox';
import { formatMinGrossMarginThresholdSubtext } from '../GrossMarginSummaryValue';
import PartOptionsModal from '../../modals/PartOptionsModal';
import { useState } from 'react';
import { OrderDrawerResponse } from '../../hooks/useGetOrderDrawer';
import PartBayButton from '../PartBayButton';
import CopyButton from '../CopyButton';
import PlaceOrderConfirmationModal from '../../modals/PlaceOrderConfirmationModal';
import { useExportSelections } from '../../hooks/useExportSelections';
import { useTogglePartExclusion } from '../../hooks/useTogglePartExclusion';
import { useQueryClient } from '@tanstack/react-query';
import { HomeFeedResponse } from '../../hooks/useHomeFeed';


interface SelectionSectionProps {
    totalParts: number;
    deliveryString?: string | null;
    orderValue?: number | null;
    grossMargin?: number | null;
    minGrossMarginThreshold?: number | null;
    parts: SelectionPart[];
    orderId: string;
    isEditable?: boolean;
    drpJob?: boolean;
}

const SelectionSectionCard = ({
    totalParts,
    deliveryString,
    orderValue,
    grossMargin,
    minGrossMarginThreshold,
    parts,
    orderId,
    isEditable = false,
    drpJob = false,
}: SelectionSectionProps) => {
    const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toggleExclusion = useTogglePartExclusion();

    const handleRowClick = (partId: string) => {
        setSelectedPartId(partId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPartId(null);
    };

    const handleExcludeToggle = async (partId: string) => {
        toggleExclusion.mutate({ orderPartId: partId, orderId: orderId, origin: 'ordering' });
    };

    return (
        <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            bg="white"
            boxShadow="none"
        >
            <Box p={4}>
                <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                    <StatBox label="TOTAL PARTS" value={totalParts} />
                    <StatBox label="DELIVERY TIME" value={deliveryString ?? '—'} />
                    <StatBox label="ORDER VALUE" value={orderValue ? formatPrice(orderValue) : '—'} />
                    <StatBox
                        label="GROSS MARGIN"
                        value={grossMargin ? formatPercentage(grossMargin) : '—'}
                        subValue={formatMinGrossMarginThresholdSubtext(minGrossMarginThreshold)}
                    />
                </Grid>
            </Box>

            <Box borderTop="1px solid" borderColor="gray.200">
                <Table variant="simple" size="sm" w="100%">
                    <Thead bg='gray.50'>
                        <Tr>
                            <Th pl={4} color="gray.500" borderColor="gray.200" w="100%">PART NAME</Th>
                            <Th color="gray.500" borderColor="gray.200" whiteSpace="nowrap">DELIVERY TIME</Th>
                            <Th isNumeric color="gray.500" borderColor="gray.200" whiteSpace="nowrap">LIST PRICE</Th>
                            <Th isNumeric pr={6} color="gray.500" borderColor="gray.200" whiteSpace="nowrap">GROSS MARGIN</Th>
                            {isEditable && <Th w="50px"></Th>}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {parts.map((part, index) => (
                            <Tr
                                key={part._id}
                                p={4}
                                _hover={{ bg: "gray.50", cursor: "pointer" }}
                                onClick={() => handleRowClick(part._id)}
                            >
                                <Td pl={4} py={3} borderColor="gray.200">
                                    <HStack spacing={3}>
                                        <SelectionStatusIcon
                                            status={part.excludeOrdering ? 'excluded' : part.flag ? 'flagged' : ''}
                                            flagReason={part.flagReason}
                                        />
                                        <VStack align="start" spacing={0}>
                                            <Text fontWeight="medium" color="gray.900">{part.title}</Text>
                                            <HStack spacing={2}>
                                                {part.partNumber ? (
                                                    <CopyButton
                                                        text={part.partNumber}
                                                        label="Copy part number"

                                                        iconAs={TbCopy as unknown as React.ComponentProps<typeof CopyButton>['iconAs']}
                                                        tooltipAnchor={
                                                            <Text fontSize="xs" color="gray.500">{part.partNumber}</Text>
                                                        }
                                                        minW="auto"
                                                        h="auto"
                                                        p={0.5}
                                                    />
                                                ) : null}
                                            </HStack>
                                        </VStack>
                                    </HStack>
                                </Td>
                                <Td py={3} fontWeight="medium" color="gray.900" borderColor="gray.200">
                                    {part.deliveryString || '-'}
                                </Td>
                                <Td isNumeric py={3} fontWeight="medium" color="gray.900" borderColor="gray.200">
                                    <ListPrice
                                        listPriceAdjusted={part.listPriceAdjusted}
                                        listPrice={part.listPrice ?? undefined}
                                    />
                                </Td>
                                <Td isNumeric pr={6} py={3} fontWeight="medium" color="gray.900" borderColor="gray.200">
                                    {part.listPrice && part.buyPrice ? formatPercentage((part.listPrice - part.buyPrice) / part.listPrice) : '-'}
                                </Td>
                                {isEditable && (
                                    <Td py={3} borderColor="gray.200" onClick={(e) => e.stopPropagation()}>
                                        <Menu>
                                            <MenuButton
                                                as={IconButton}
                                                aria-label="More options"
                                                icon={<Icon as={TbDots as React.ElementType} w={4} h={4} />}
                                                size="sm"
                                                variant="ghost"
                                                color="gray.500"
                                            />
                                            <MenuList>
                                                <MenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleExcludeToggle(part._id);
                                                    }}
                                                    isDisabled={toggleExclusion.isPending}
                                                    sx={{
                                                        _hover: {
                                                            bg: 'transparent',
                                                            color: '#0071E3'
                                                        },
                                                        _active: {
                                                            bg: 'transparent',
                                                            color: '#0071E3'
                                                        },
                                                        _focus: {
                                                            bg: 'transparent',
                                                            color: '#0071E3'
                                                        }
                                                    }}
                                                >
                                                    <HStack spacing={2}>
                                                        <Icon as={(part.excludeOrdering ? TbCircleCheck : TbCircleX) as React.ElementType} w={4} h={4} />
                                                        <Text>{part.excludeOrdering ? 'Include' : 'Exclude'}{toggleExclusion.isPending && ' (Updating...)'}</Text>
                                                    </HStack>
                                                </MenuItem>
                                            </MenuList>
                                        </Menu>
                                    </Td>
                                )}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            {selectedPartId &&
                <PartOptionsModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    orderPartId={selectedPartId}
                    orderId={orderId}
                    source='order'
                    drpJob={drpJob}
                />}
        </Box>
    );
};

interface SelectionTabPanelProps {
    data: NonNullable<OrderDrawerResponse['selection']>;
    orderId: string;
    potentialTotal: boolean;
    quotesOnly: boolean;
    minGrossMarginThreshold?: number | null;
    drpJob?: boolean;
}

const SelectionTabPanel = ({ data, orderId, potentialTotal, quotesOnly, minGrossMarginThreshold, drpJob = false }: SelectionTabPanelProps) => {
    const exportSelections = useExportSelections();
    const [isPlaceOrderConfirmationOpen, setIsPlaceOrderConfirmationOpen] = useState(false);
    const toast = useToast();
    const queryClient = useQueryClient();
    const isQuoteWorkflow = quotesOnly || potentialTotal;

    const handleExportSelections = async () => {
        if (!orderId) return;
        try {
            await exportSelections.mutateAsync({ orderId });
            toast({
                title: "Selections export queued!",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Failed to export selections",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handlePlaceOrderClick = () => {
        setIsPlaceOrderConfirmationOpen(true);
    };

    const handleClosePlaceOrderConfirmation = () => {
        setIsPlaceOrderConfirmationOpen(false);
        queryClient.setQueriesData<HomeFeedResponse>({ queryKey: ['/orders/home-feed-paginated'] }, (old) => {
            if (!old) return old;
            return {
                ...old,
                orders: old.orders.filter(order => order._id !== orderId)
            };
        });
    };

    return (
        <>
            <Tabs variant="unstyled" colorScheme="blue">
                <Flex justify="space-between" align="flex-end" mb={4} borderBottom="1.5px solid" borderColor="gray.200">
                    <TabList border="none" gap={0}>
                        {data.flagged && <Tab
                            fontWeight="medium"
                            fontSize="sm"
                            color="gray.500"
                            bg="transparent"
                            border="none"
                            borderBottom="1.5px solid"
                            borderColor="transparent"
                            mb="-1.5px"
                            py={2}
                            px={3}
                            _selected={{
                                color: "gray.800",
                                borderColor: "gray.500",
                            }}
                            _hover={{
                                color: "gray.700",
                            }}
                        >
                            <HStack spacing={0}>
                                <Text>Flagged</Text>
                                {data.flagged.totalParts > 0 && (
                                    <Flex
                                        ml={1.5}
                                        bg="gray.100"
                                        borderRadius="lg"
                                        h="18px"
                                        minW="18px"
                                        px="5px"
                                        align="center"
                                        justify="center"
                                        flexShrink={0}
                                    >
                                        <Text
                                            fontSize="9px"
                                            fontWeight="bold"
                                            lineHeight="1"
                                            color="gray.800"
                                        >
                                            {data.flagged.totalParts}
                                        </Text>
                                    </Flex>
                                )}
                            </HStack>
                        </Tab>}
                        {data.pending && <Tab
                            fontWeight="medium"
                            fontSize="sm"
                            color="gray.500"
                            bg="transparent"
                            border="none"
                            borderBottom="1.5px solid"
                            borderColor="transparent"
                            mb="-1.5px"
                            py={2}
                            px={3}
                            _selected={{
                                color: "gray.800",
                                borderColor: "gray.500",
                            }}
                            _hover={{
                                color: "gray.700",
                            }}
                        >
                            <HStack spacing={0}>
                                <Text>Pending</Text>
                                {data.pending.totalParts > 0 && (
                                    <Flex
                                        ml={1.5}
                                        bg="gray.100"
                                        borderRadius="lg"
                                        h="18px"
                                        minW="18px"
                                        px="5px"
                                        align="center"
                                        justify="center"
                                        flexShrink={0}
                                    >
                                        <Text
                                            fontSize="9px"
                                            fontWeight="bold"
                                            lineHeight="1"
                                            color="gray.800"
                                        >
                                            {data.pending.totalParts}
                                        </Text>
                                    </Flex>
                                )}
                            </HStack>
                        </Tab>}
                        {data.noQuoted && <Tab
                            fontWeight="medium"
                            fontSize="sm"
                            color="gray.500"
                            bg="transparent"
                            border="none"
                            borderBottom="1.5px solid"
                            borderColor="transparent"
                            mb="-1.5px"
                            py={2}
                            px={3}
                            _selected={{
                                color: "gray.800",
                                borderColor: "gray.500",
                            }}
                            _hover={{
                                color: "gray.700",
                            }}
                        >
                            <HStack spacing={0}>
                                <Text>No Quote</Text>
                                {data.noQuoted.totalParts > 0 && (
                                    <Flex
                                        ml={1.5}
                                        bg="gray.100"
                                        borderRadius="lg"
                                        h="18px"
                                        minW="18px"
                                        px="5px"
                                        align="center"
                                        justify="center"
                                        flexShrink={0}
                                    >
                                        <Text
                                            fontSize="9px"
                                            fontWeight="bold"
                                            lineHeight="1"
                                            color="gray.800"
                                        >
                                            {data.noQuoted.totalParts}
                                        </Text>
                                    </Flex>
                                )}
                            </HStack>
                        </Tab>}
                    </TabList>
                    <HStack spacing={3} pb={2}>
                        {isQuoteWorkflow ? (
                            <>
                                <Menu placement="bottom-end">
                                    <MenuButton
                                        as={Button}
                                        size="sm"
                                        variant="outline"
                                        colorScheme="gray"
                                        bg="white"
                                        rightIcon={<ChevronDownIcon />}
                                    >
                                        More Actions
                                    </MenuButton>
                                    <MenuList py={1} minW="200px">
                                        <MenuItem
                                            onClick={handlePlaceOrderClick}
                                            isDisabled={isPlaceOrderConfirmationOpen}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={FiShoppingCart as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Place Order</Text>
                                            </HStack>
                                        </MenuItem>
                                    </MenuList>
                                </Menu>
                                <PartBayButton
                                    variant="outline"
                                    size="sm"
                                    onClick={handleExportSelections}
                                    isLoading={exportSelections.isPending}
                                    loadingText="Exporting..."
                                    text="Export Selections"
                                    colorScheme="partbay-blue"
                                />
                            </>
                        ) : (
                            <PartBayButton
                                variant="solid"
                                size="sm"
                                onClick={handlePlaceOrderClick}
                                text="Place Order"
                                colorScheme="partbay-blue"
                                isDisabled={isPlaceOrderConfirmationOpen}
                            />
                        )}
                    </HStack>
                </Flex>

                <TabPanels>
                    {data.flagged && (
                        <TabPanel px={0} py={0}>
                            <SelectionSectionCard
                                totalParts={data.flagged.totalParts}
                                deliveryString={data.flagged.deliveryString}
                                orderValue={data.flagged.orderValue}
                                grossMargin={data.flagged.grossMargin}
                                minGrossMarginThreshold={minGrossMarginThreshold}
                                parts={data.flagged.parts}
                                orderId={orderId || ''}
                                isEditable={true}
                                drpJob={drpJob}
                            />
                        </TabPanel>
                    )}
                    {data.pending && (
                        <TabPanel px={0} py={0}>
                            <SelectionSectionCard
                                totalParts={data.pending.totalParts}
                                deliveryString={data.pending.deliveryString}
                                orderValue={data.pending.orderValue}
                                grossMargin={data.pending.grossMargin}
                                minGrossMarginThreshold={minGrossMarginThreshold}
                                parts={data.pending.parts}
                                orderId={orderId || ''}
                                isEditable={true}
                                drpJob={drpJob}
                            />
                        </TabPanel>
                    )}
                    {data.noQuoted && (
                        <TabPanel px={0} py={0}>
                            <Box
                                borderWidth="1px"
                                borderColor="gray.200"
                                borderRadius="lg"
                                overflow="hidden"
                                bg="white"
                                boxShadow="none"
                            >
                                <Table variant="simple" size="sm" w="100%">
                                    <Thead bg='gray.50'>
                                        <Tr>
                                            <Th pl={4} color="gray.500" borderColor="gray.200">PART NAME</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {data.noQuoted.parts.map((part) => (
                                            <Tr key={part._id}>
                                                <Td pl={4} py={3} borderColor="gray.200">
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontWeight="medium" color="gray.900">{part.title}</Text>
                                                        <Text fontSize="xs" color="gray.500">{part.partNumber}</Text>
                                                    </VStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </TabPanel>
                    )}
                </TabPanels>
            </Tabs>
            {orderId && (
                <PlaceOrderConfirmationModal
                    isOpen={isPlaceOrderConfirmationOpen}
                    onClose={handleClosePlaceOrderConfirmation}
                    orderId={orderId}
                />
            )}
        </>
    );
};

export default SelectionTabPanel;

